/**
 * EPL Lab Wizard - Asistente paso a paso para crear laboratorios
 * 
 * Pasos del wizard:
 * 1. Metadata - Información básica del laboratorio
 * 2. Campos - Diseño de formulario con Field Designer
 * 3. Validaciones - Configuración de reglas de negocio
 * 4. Preview - Vista previa completa del laboratorio
 * 5. Publish - Publicación y configuraciones finales
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  Eye, 
  Settings, 
  FileText, 
  Users, 
  Beaker,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';

import EplFieldDesigner from '../components/EplFieldDesigner';
import { useEplLab, useEplValidation } from '../hooks/useEpl';
import { eplApi } from '../services/epl_api';

const WIZARD_STEPS = [
  { 
    id: 'metadata', 
    title: 'Información Básica', 
    icon: FileText,
    description: 'Título, descripción y configuración general'
  },
  { 
    id: 'fields', 
    title: 'Diseño del Formulario', 
    icon: Settings,
    description: 'Crear y configurar campos del laboratorio'
  },
  { 
    id: 'validation', 
    title: 'Validaciones', 
    icon: CheckCircle,
    description: 'Reglas de negocio y validación de datos'
  },
  { 
    id: 'preview', 
    title: 'Vista Previa', 
    icon: Eye,
    description: 'Revisar el laboratorio antes de publicar'
  },
  { 
    id: 'publish', 
    title: 'Publicar', 
    icon: Beaker,
    description: 'Configuraciones finales y publicación'
  }
];

const DEFAULT_LAB_DATA = {
  title: '',
  description: '',
  category: 'education',
  systemCard: 'innovafast',
  estimatedTime: 15,
  difficulty: 'beginner',
  tags: [],
  fields: [],
  aiConfig: {
    enableAI: true,
    evaluationPrompt: '',
    feedbackStyle: 'detailed'
  },
  settings: {
    allowAnonymous: true,
    requireAuth: false,
    maxAttempts: 0,
    timeLimit: 0
  },
  status: 'draft'
};

const EplLabWizard = () => {
  const navigate = useNavigate();
  const { labId } = useParams();
  const isEditing = Boolean(labId);

  const [currentStep, setCurrentStep] = useState(0);
  const [labData, setLabData] = useState(DEFAULT_LAB_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const { lab, loading, error, actions } = useEplLab(labId);
  const { isValidating, validationErrors, validateLabConfig } = useEplValidation();

  // Función para generar UUID v4 válido
  const generateUuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Función para validar si un string es UUID válido
  const isValidUuid = (str) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Función para normalizar campos asegurando UUIDs válidos
  const normalizeFields = (fields) => {
    if (!Array.isArray(fields)) return [];
    
    return fields.map(field => {
      console.log('🔍 [normalizeFields] Field:', field.id, 'Type:', field.type, 'premiumConfig:', field.premiumConfig);
      return {
        ...field,
        id: isValidUuid(field.id) ? field.id : generateUuid()
      };
    });
  };

  // Cargar datos del laboratorio si estamos editando
  useEffect(() => {
    if (isEditing && lab) {
      console.log('🔄 [DEBUG] Loading existing lab:', lab);
      console.log('🔄 [DEBUG] Lab metadata:', lab.metadata);
      console.log('🔄 [DEBUG] Lab aiConfig:', lab.aiConfig);
      
      // Merge seguro con valores por defecto para evitar propiedades undefined
      const mergedData = {
        ...DEFAULT_LAB_DATA,
        title: lab.title || lab.name || '', // Usar 'name' si 'title' no existe
        description: lab.description || '',
        category: lab.category || 'education',
        systemCard: lab.systemCard || 'innovafast',
        // ✅ Extraer difficulty de metadata (nuevo formato backend)
        difficulty: lab.metadata?.difficulty || lab.difficulty || 'beginner',
        estimatedTime: lab.metadata?.estimatedTime || lab.estimatedTime || 15,
        tags: lab.metadata?.tags || lab.tags || [],
        fields: normalizeFields(lab.fields || []), // Normalizar campos con UUIDs válidos
        // ✅ Cargar aiConfig completo
        aiConfig: {
          enableAI: lab.aiConfig?.enableAI ?? DEFAULT_LAB_DATA.aiConfig.enableAI,
          evaluationPrompt: lab.aiConfig?.evaluationPrompt ?? '',
          feedbackStyle: lab.aiConfig?.feedbackStyle ?? 'detailed'
        },
        settings: lab.settings || DEFAULT_LAB_DATA.settings,
        status: lab.status || 'draft'
      };
      
      console.log('🔄 [DEBUG] Merged lab data:', mergedData);
      console.log('🔄 [DEBUG] Final difficulty:', mergedData.difficulty);
      console.log('🔄 [DEBUG] Final aiConfig:', mergedData.aiConfig);
      
      setLabData(mergedData);
    }
  }, [isEditing, lab]);

  const currentStepData = WIZARD_STEPS[currentStep];

  const updateLabData = (updates) => {
    console.log('🔧 [DEBUG] updateLabData called with:', updates);
    console.log('🔧 [DEBUG] Previous labData:', labData);
    setLabData(prev => {
      const newData = { ...prev, ...updates };
      console.log('🔧 [DEBUG] New labData after merge:', newData);
      console.log('🔧 [DEBUG] New aiConfig:', newData.aiConfig);
      return newData;
    });
  };

  const canGoNext = () => {
    switch (currentStepData.id) {
      case 'metadata':
        return labData.title?.trim() && labData.description?.trim();
      case 'fields':
        return labData.fields?.length > 0;
      case 'validation':
        return true; // Las validaciones son opcionales
      case 'preview':
        return Object.keys(validationErrors).length === 0;
      default:
        return true;
    }
  };

  const canGoPrevious = () => currentStep > 0;

  const handleNext = async () => {
    if (currentStepData.id === 'preview') {
      // Validar antes de ir a publish
      await validateLabConfig(labData);
    }
    
    if (canGoNext() && currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (canGoPrevious()) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);

      // Mapear datos del frontend al formato esperado por el backend
      const fields = (labData.structure || labData.fields || []).map(field => {
        // Convertir options de string[] a {label, value}[] si es necesario
        let options = [];
        if (field.options) {
          if (Array.isArray(field.options)) {
            options = field.options.map(option => {
              // Si ya es un objeto con label y value, mantenerlo
              if (typeof option === 'object' && option !== null && option.label !== undefined && option.value !== undefined) {
                return {
                  label: String(option.label),
                  value: option.value
                };
              }
              // Si es string, convertir a objeto
              if (typeof option === 'string' && option.trim() !== '') {
                return { label: option, value: option };
              }
              // Si es número, convertir a objeto
              if (typeof option === 'number') {
                return { label: String(option), value: option };
              }
              // Fallback para otros tipos
              const stringValue = String(option);
              return { label: stringValue, value: stringValue };
            });
            // Filtrar opciones vacías o inválidas
            options = options.filter(opt => opt.label && opt.label.trim() !== '');
          }
        }

        // Asegurar que cada campo tenga las propiedades requeridas
        // Preservar TODAS las propiedades incluyendo premiumConfig, validation, description
        const processedField = {
          id: field.id,
          name: field.name || field.id || `field_${Date.now()}`,
          type: field.type || 'text',
          label: field.label || field.name || 'Campo',
          description: field.description || '', // ← Preservar descripción
          required: field.required || false,
          placeholder: field.placeholder || '',
          defaultValue: field.defaultValue,
          options: options, // ← Usar las options convertidas
          validation: field.validation || {}, // ← Preservar validaciones
          premiumConfig: field.premiumConfig || {}, // ← CRÍTICO: Preservar configuración premium (rowLabels, columnLabels, etc)
          order: field.order,
          visible: field.visible !== false,
          metadata: field.metadata || {}
        };

        console.log('💾 [handleSaveDraft] Processed field:', processedField.id, 'Type:', processedField.type, 'premiumConfig:', processedField.premiumConfig);

        return processedField;
      });

      const labToSave = {
        name: labData.title || labData.name, // Backend espera 'name', no 'title'
        description: labData.description || '',
        category: labData.category || 'education', // Usar categoría válida del backend
        systemCard: labData.systemCard || 'transforma360',
        fields: fields, // Campos validados y formateados
        status: labData.status || 'draft', // ← Respetar el status seleccionado
        metadata: {
          difficulty: labData.difficulty || 'beginner', // ✅ Guardar en metadata
          estimatedTime: labData.estimatedTime || 15,
          tags: labData.tags || []
        },
        aiConfig: {
          enableAI: labData.aiConfig?.enableAI ?? true,
          evaluationPrompt: labData.aiConfig?.evaluationPrompt ?? '',
          feedbackStyle: labData.aiConfig?.feedbackStyle ?? 'detailed'
        },
        settings: labData.settings || {}
      };

      console.log('📤 [DEBUG] labToSave ANTES de enviar al backend:', JSON.stringify(labToSave, null, 2));
      console.log('📤 [DEBUG] aiConfig que se enviará:', labToSave.aiConfig);

      if (isEditing) {
        await eplApi.updateLab(labId, labToSave);
      } else {
        const response = await eplApi.createLab(labToSave);
        // Redirigir a edición si se creó nuevo lab
        if (response.data?.id) {
          navigate(`/epl/builder/edit/${response.data.id}`, { replace: true });
        }
      }
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const processFieldForBackend = (field) => {
    // Asegurar UUID válido
    const validId = isValidUuid(field.id) ? field.id : generateUuid();
    console.log(`🔍 Campo procesado - ID original: ${field.id}, ID válido: ${validId}`);

    // Convertir options de string[] a {label, value}[] si es necesario
    let options = [];
    if (field.options) {
      if (Array.isArray(field.options)) {
        options = field.options.map(option => {
          if (typeof option === 'object' && option !== null && option.label !== undefined && option.value !== undefined) {
            return { label: String(option.label), value: option.value };
          }
          if (typeof option === 'string' && option.trim() !== '') {
            return { label: option, value: option };
          }
          return { label: String(option), value: String(option) };
        }).filter(opt => opt.label && opt.label.trim() !== '');
      }
    }

    return {
      id: validId,
      name: field.name || validId || `field_${Date.now()}`,
      type: field.type || 'text',
      label: field.label || field.name || 'Campo',
      description: field.description || '', // ← Preservar descripción
      required: field.required || false,
      placeholder: field.placeholder || '',
      defaultValue: field.defaultValue,
      options: options,
      validation: field.validation || {}, // ← Preservar validaciones
      premiumConfig: field.premiumConfig || {}, // ← CRÍTICO: Preservar configuración premium
      order: field.order || 0,
      validations: field.validations || [],
      visible: field.visible !== false,
      metadata: field.metadata || {}
    };
  };

  const handlePublish = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);

      // Mapear datos del frontend al formato esperado por el backend
      const fields = (labData.structure || labData.fields || []).map(field => {
        // Convertir options de string[] a {label, value}[] si es necesario
        let options = [];
        if (field.options) {
          if (Array.isArray(field.options)) {
            options = field.options.map(option => {
              // Si ya es un objeto con label y value, mantenerlo
              if (typeof option === 'object' && option !== null && option.label !== undefined && option.value !== undefined) {
                return {
                  label: String(option.label),
                  value: option.value
                };
              }
              // Si es string, convertir a objeto
              if (typeof option === 'string' && option.trim() !== '') {
                return { label: option, value: option };
              }
              // Si es número, convertir a objeto
              if (typeof option === 'number') {
                return { label: String(option), value: option };
              }
              // Fallback para otros tipos
              const stringValue = String(option);
              return { label: stringValue, value: stringValue };
            });
            // Filtrar opciones vacías o inválidas
            options = options.filter(opt => opt.label && opt.label.trim() !== '');
          }
        }

        // Asegurar que cada campo tenga las propiedades requeridas
        // Preservar TODAS las propiedades incluyendo premiumConfig, validation, description
        const processedField = {
          id: field.id,
          name: field.name || field.id || `field_${Date.now()}`,
          type: field.type || 'text',
          label: field.label || field.name || 'Campo',
          description: field.description || '', // ← Preservar descripción
          required: field.required || false,
          placeholder: field.placeholder || '',
          defaultValue: field.defaultValue,
          options: options, // ← Usar las options convertidas
          validation: field.validation || {}, // ← Preservar validaciones
          premiumConfig: field.premiumConfig || {}, // ← CRÍTICO: Preservar configuración premium (rowLabels, columnLabels, etc)
          order: field.order,
          visible: field.visible !== false,
          metadata: field.metadata || {}
        };

        return processedField;
      });

      console.log('🧪 DEBUG - Raw labData:', labData);
      console.log('🧪 DEBUG - Raw structure:', labData.structure);
      console.log('🧪 DEBUG - Processed fields:', fields);
      
      // Debug específico de options
      fields.forEach((field, index) => {
        if (field.options) {
          console.log(`🧪 DEBUG - Field ${index} (${field.name}) options:`, field.options);
          console.log(`🧪 DEBUG - Options type:`, typeof field.options);
          console.log(`🧪 DEBUG - Options array check:`, Array.isArray(field.options));
          if (Array.isArray(field.options)) {
            field.options.forEach((opt, optIndex) => {
              console.log(`🧪 DEBUG - Option ${optIndex}:`, opt, 'type:', typeof opt);
            });
          }
        }
      });

      console.log('🧪 DEBUG - FINAL PAYLOAD antes de enviar:', JSON.stringify(fields, null, 2));

      const labToPublish = {
        name: labData.title || labData.name, // Backend espera 'name', no 'title'
        description: labData.description || '',
        category: labData.category || 'education', // Usar categoría válida del backend
        systemCard: labData.systemCard || 'transforma360',
        fields: fields, // Campos validados y formateados
        status: labData.status || 'active', // ← Respetar el status seleccionado
        metadata: {
          difficulty: labData.difficulty || 'beginner',
          estimatedTime: labData.estimatedTime || 15,
          tags: labData.tags || []
        },
        aiConfig: {
          enableAI: labData.aiConfig?.enableAI ?? true,
          evaluationPrompt: labData.aiConfig?.evaluationPrompt ?? '',
          feedbackStyle: labData.aiConfig?.feedbackStyle ?? 'detailed'
        },
        settings: labData.settings || {}
      };

      console.log('📤 [DEBUG] labToPublish ANTES de enviar:', JSON.stringify(labToPublish, null, 2));
      console.log('📤 [DEBUG] aiConfig que se enviará:', labToPublish.aiConfig);

      if (isEditing) {
        console.log('🔄 Actualizando lab:', labId, labToPublish);
        const response = await eplApi.updateLab(labId, labToPublish);
        console.log('✅ Lab actualizado exitosamente:', response);
      } else {
        console.log('🆕 Creando nuevo lab:', labToPublish);
        const response = await eplApi.createLab(labToPublish);
        console.log('✅ Lab creado exitosamente:', response);
      }

      // Redirigir al dashboard después de publicar
      console.log('🔄 Navegando al dashboard...');
      navigate('/epl/builder');
    } catch (err) {
      console.error('❌ Error en handlePublish:', err);
      setSaveError(`Error al ${isEditing ? 'actualizar' : 'crear'} laboratorio: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Cargando laboratorio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error al cargar</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/epl/builder')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header del wizard */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/epl/builder')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="w-5 h-5" />
                Volver al Dashboard
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {isEditing ? `Editar: ${labData.title || 'Laboratorio'}` : 'Nuevo Laboratorio'}
                </h1>
                <p className="text-sm text-gray-600">
                  Paso {currentStep + 1} de {WIZARD_STEPS.length}: {currentStepData.title}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveDraft}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Borrador
              </button>

              {saveError && (
                <div className="text-sm text-red-600">
                  {saveError}
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center">
              {WIZARD_STEPS.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                
                return (
                  <React.Fragment key={step.id}>
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                      isActive ? 'bg-blue-100 text-blue-700' :
                      isCompleted ? 'bg-green-100 text-green-700' :
                      'text-gray-500'
                    }`}>
                      <StepIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">{step.title}</span>
                    </div>
                    {index < WIZARD_STEPS.length - 1 && (
                      <ChevronRight className="w-5 h-5 text-gray-400 mx-2" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Contenido del paso actual */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border min-h-96">
          {renderStepContent()}
        </div>
      </div>

      {/* Controles de navegación */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious()}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </button>

          <div className="text-sm text-gray-600">
            {currentStep + 1} de {WIZARD_STEPS.length}
          </div>

          {currentStep === WIZARD_STEPS.length - 1 ? (
            <button
              onClick={handlePublish}
              disabled={isSaving || !canGoNext()}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Beaker className="w-4 h-4" />}
              Publicar Laboratorio
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canGoNext()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  function renderStepContent() {
    switch (currentStepData.id) {
      case 'metadata':
        return <MetadataStep data={labData} onChange={updateLabData} />;
      
      case 'fields':
        return (
          <EplFieldDesigner
            fields={labData.fields}
            onChange={(fields) => updateLabData({ fields })}
          />
        );
      
      case 'validation':
        return <ValidationStep data={labData} onChange={updateLabData} />;
      
      case 'preview':
        return <PreviewStep data={labData} validationErrors={validationErrors} />;
      
      case 'publish':
        return <PublishStep data={labData} onChange={updateLabData} />;
      
      default:
        return <div className="p-6">Paso no implementado</div>;
    }
  }
};

// Componente para el paso de metadata
const MetadataStep = ({ data, onChange }) => {
  const [tagInput, setTagInput] = React.useState('');

  React.useEffect(() => {
    // Sincronizar el input con los tags existentes cuando se monta el componente
    if (data.tags && data.tags.length > 0) {
      setTagInput(data.tags.join(', '));
    }
  }, []);

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setTagInput(value);
    
    // Procesar tags en tiempo real
    const tags = value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    onChange({ tags });
  };

  const handleTagInputBlur = () => {
    // Normalizar el input cuando pierde el foco
    if (data.tags && data.tags.length > 0) {
      setTagInput(data.tags.join(', '));
    } else {
      setTagInput('');
    }
  };

  const removeTag = (indexToRemove) => {
    const newTags = data.tags.filter((_, index) => index !== indexToRemove);
    onChange({ tags: newTags });
    setTagInput(newTags.join(', '));
  };

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Información Básica</h2>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Título del Laboratorio *
          </label>
          <input
            type="text"
            value={data.title}
            onChange={(e) => onChange({ title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ej: Análisis FODA Empresarial"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descripción *
          </label>
          <textarea
            value={data.description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe el objetivo y contenido del laboratorio..."
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sistema / Módulo *
            </label>
            <select
              value={data.systemCard}
              onChange={(e) => onChange({ systemCard: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <optgroup label="EPL-Leader Lab">
                <option value="gestion_crisis">Gestión de Crisis</option>
                <option value="dinamicas_equipo">Dinámicas de Equipo</option>
                <option value="comunicacion_estrategica">Comunicación Estratégica</option>
              </optgroup>
              <optgroup label="EPL-Innova System">
                <option value="innovafast">InnovaFast</option>
                <option value="innovatech">InnovaTech</option>
                <option value="transforma360">Transforma360</option>
              </optgroup>
              <optgroup label="EPL-Decision System">
                <option value="dss_builder">DSS Builder</option>
                <option value="analisis_riesgo">Análisis de Riesgo</option>
                <option value="planificacion_estrategica">Planificación Estratégica</option>
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categoría
            </label>
            <select
              value={data.category}
              onChange={(e) => onChange({ category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="education">Educación</option>
              <option value="business">Negocios</option>
              <option value="research">Investigación</option>
              <option value="innovation">Innovación</option>
              <option value="assessment">Evaluación</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tiempo Estimado (minutos)
            </label>
            <input
              type="number"
              min="5"
              max="180"
              value={data.estimatedTime}
              onChange={(e) => onChange({ estimatedTime: parseInt(e.target.value) || 15 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nivel de Dificultad
          </label>
          <select
            value={data.difficulty}
            onChange={(e) => {
              console.log('⚠️ [DEBUG] Difficulty changed to:', e.target.value);
              onChange({ difficulty: e.target.value });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="beginner">Principiante</option>
            <option value="intermediate">Intermedio</option>
            <option value="advanced">Avanzado</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Etiquetas (separadas por coma)
          </label>
          <input
            type="text"
            value={tagInput}
            onChange={handleTagInputChange}
            onBlur={handleTagInputBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ej: estrategia, análisis, negocios"
          />
          {data.tags && data.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {data.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(index)}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                  >
                    <span className="text-blue-600">×</span>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente para el paso de validación
const ValidationStep = ({ data, onChange }) => {
  const [showPromptGuide, setShowPromptGuide] = useState(false);
  
  const updateAIConfig = (key, value) => {
    console.log('🤖 [DEBUG] updateAIConfig called with:', key, '=', value);
    console.log('🤖 [DEBUG] Current aiConfig:', data.aiConfig);
    const newAIConfig = { ...data.aiConfig, [key]: value };
    console.log('🤖 [DEBUG] New aiConfig:', newAIConfig);
    onChange({
      aiConfig: newAIConfig
    });
  };

  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuración de IA y Validaciones</h2>
      
      <div className="space-y-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="enableAI"
            checked={data.aiConfig.enableAI}
            onChange={(e) => updateAIConfig('enableAI', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="enableAI" className="ml-3 block text-sm font-medium text-gray-700">
            Habilitar evaluación con IA
          </label>
        </div>

        {data.aiConfig.enableAI && (
          <>
            {/* GUÍA INTERACTIVA DE PROMPTING */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <button
                onClick={() => setShowPromptGuide(!showPromptGuide)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    📚 Guía de Construcción de Prompts
                  </span>
                </div>
                <ChevronRight className={`w-5 h-5 text-blue-600 transition-transform ${showPromptGuide ? 'rotate-90' : ''}`} />
              </button>
              
              {showPromptGuide && (
                <div className="mt-4 space-y-4 text-sm">
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-gray-900 mb-2">🎯 ¿Qué debes escribir en tu prompt?</h4>
                    <p className="text-gray-700 mb-3">
                      Escribe SOLO el <strong>rol y contexto pedagógico específico</strong> de tu laboratorio.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded p-3 mb-2">
                      <p className="text-xs font-semibold text-green-800 mb-1">✅ EJEMPLO CORRECTO:</p>
                      <code className="text-xs text-green-900">
                        "Eres un experto en metodología Lean Startup evaluando hipótesis de negocio. 
                        Verifica que el estudiante entienda la estructura cliente-problema-solución."
                      </code>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-xs font-semibold text-red-800 mb-1">❌ EVITA ESCRIBIR:</p>
                      <code className="text-xs text-red-900">
                        "Califica del 0-100, proporciona feedback detallado con recomendaciones..."
                      </code>
                      <p className="text-xs text-red-700 mt-1">↑ Esto ya está automático en el sistema</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-gray-900 mb-2">🤖 El sistema añade automáticamente:</h4>
                    <ul className="space-y-1 text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span><strong>Formato JSON:</strong> Estructura de respuesta (score, feedback, recommendations)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span><strong>Estilo de feedback:</strong> Según tu selección (detallado/breve/alentador/analítico)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span><strong>Criterios generales:</strong> Relevancia, profundidad, coherencia, creatividad</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span><strong>Metadata del lab:</strong> Título, descripción, categoría, dificultad</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span><strong>Preguntas + respuestas:</strong> Todos los campos del formulario con respuestas del estudiante</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">💡 Vista Previa del Prompt Final</h4>
                    <div className="bg-gray-900 text-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                      <div className="text-green-400">// TU PROMPT (personalizado):</div>
                      <div className="text-white mb-2">{data.aiConfig.evaluationPrompt || '[Tu prompt aquí...]'}</div>
                      
                      <div className="text-blue-400 mt-3">// + SISTEMA AÑADE (automático):</div>
                      <div className="text-gray-400">
                        {data.aiConfig.feedbackStyle === 'detailed' && '→ Feedback DETALLADO: 200+ palabras, análisis profundo'}
                        {data.aiConfig.feedbackStyle === 'brief' && '→ Feedback BREVE: Máx 100 palabras, puntos clave'}
                        {data.aiConfig.feedbackStyle === 'encouraging' && '→ Feedback ALENTADOR: Positivo, motivacional'}
                        {data.aiConfig.feedbackStyle === 'analytical' && '→ Feedback ANALÍTICO: Métricas, objetivo'}
                      </div>
                      <div className="text-gray-400">→ Formato: JSON con score (0-100) + feedback + recommendations</div>
                      <div className="text-gray-400">→ Criterios: Relevancia, profundidad, coherencia</div>
                      <div className="text-gray-400">→ Metadata: {data.name}, {data.category}, {data.metadata?.difficulty}</div>
                      <div className="text-gray-400">→ Preguntas: {data.fields?.length || 0} campos con respuestas del estudiante</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt de Evaluación (Solo Rol y Contexto Pedagógico)
              </label>
              <textarea
                value={data.aiConfig.evaluationPrompt}
                onChange={(e) => updateAIConfig('evaluationPrompt', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder="Ejemplo: Eres un experto en [TU DOMINIO] evaluando [OBJETIVO ESPECÍFICO]. Verifica que el estudiante demuestre comprensión de [CONCEPTOS CLAVE]..."
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Define el rol del evaluador y los criterios específicos de tu dominio. El sistema se encarga del resto.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estilo de Feedback
              </label>
              <select
                value={data.aiConfig.feedbackStyle}
                onChange={(e) => {
                  console.log('💬 [DEBUG] FeedbackStyle changed to:', e.target.value);
                  console.log('💬 [DEBUG] Current aiConfig:', data.aiConfig);
                  updateAIConfig('feedbackStyle', e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="detailed">Detallado con sugerencias</option>
                <option value="brief">Breve y directo</option>
                <option value="encouraging">Alentador y positivo</option>
                <option value="analytical">Analítico y crítico</option>
              </select>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Componente para el paso de vista previa
const PreviewStep = ({ data, validationErrors }) => {
  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Vista Previa del Laboratorio</h2>
      
      {Object.keys(validationErrors).length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-medium mb-2">Errores de validación:</h3>
          <ul className="list-disc list-inside text-red-700 text-sm">
            {Object.entries(validationErrors).map(([field, error]) => (
              <li key={field}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-gray-50 p-6 rounded-lg">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-900">{data.title}</h3>
          <p className="text-gray-600 mt-2">{data.description}</p>
          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
            <span>🏷️ {data.category}</span>
            <span>⏱️ {data.estimatedTime} min</span>
            <span>📊 {data.difficulty}</span>
          </div>
        </div>

        <EplFieldDesigner fields={data.fields} preview={true} />
      </div>
    </div>
  );
};

// Componente para el paso de publicación
const PublishStep = ({ data, onChange }) => {
  const updateSettings = (key, value) => {
    onChange({
      settings: { ...data.settings, [key]: value }
    });
  };

  const updateStatus = (value) => {
    onChange({ status: value });
  };

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuración de Publicación</h2>
      
      <div className="space-y-6">
        {/* Selector de Estado del Laboratorio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estado del Laboratorio
          </label>
          <select
            value={data.status || 'draft'}
            onChange={(e) => updateStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="draft">Borrador - Solo visible para ti</option>
            <option value="active">Activo - Disponible para estudiantes</option>
            <option value="disabled">Deshabilitado - Visible pero no ejecutable</option>
            <option value="archived">Archivado - Oculto para estudiantes</option>
            <option value="maintenance">Mantenimiento - Temporalmente no disponible</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {data.status === 'draft' && '📝 El lab no será visible para estudiantes'}
            {data.status === 'active' && '✅ Estudiantes pueden ver y ejecutar este lab'}
            {data.status === 'disabled' && '🔒 Estudiantes pueden ver pero NO ejecutar este lab'}
            {data.status === 'archived' && '📦 El lab está archivado y oculto'}
            {data.status === 'maintenance' && '🔧 El lab está temporalmente no disponible'}
          </p>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="allowAnonymous"
            checked={data.settings.allowAnonymous}
            onChange={(e) => updateSettings('allowAnonymous', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="allowAnonymous" className="ml-3 block text-sm font-medium text-gray-700">
            Permitir acceso anónimo
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="requireAuth"
            checked={data.settings.requireAuth}
            onChange={(e) => updateSettings('requireAuth', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="requireAuth" className="ml-3 block text-sm font-medium text-gray-700">
            Requerir autenticación
          </label>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Máximo intentos (0 = ilimitado)
            </label>
            <input
              type="number"
              min="0"
              value={data.settings.maxAttempts}
              onChange={(e) => updateSettings('maxAttempts', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Límite de tiempo (min, 0 = sin límite)
            </label>
            <input
              type="number"
              min="0"
              value={data.settings.timeLimit}
              onChange={(e) => updateSettings('timeLimit', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-blue-800 font-medium mb-2">¿Listo para publicar?</h3>
          <p className="text-blue-700 text-sm">
            Al publicar, el laboratorio estará disponible para los usuarios. 
            Puedes editarlo después, pero los cambios podrían afectar las sesiones activas.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EplLabWizard;